import { ApiError } from 'src/api/client';
import type { EditorialStreamSummary, PipelineJob, PipelineJobStatus, PipelineStage } from 'src/types/editorial';
import { PIPELINE_STAGES } from 'src/types/editorial';
import type { Tone } from 'src/lib/tone';

/**
 * What each stage produces, and what it costs to run. The picker states both, because "run the
 * next section" is a spending decision as much as an editorial one: the first four stages run on
 * the box, the last two call paid APIs — the X review alone is around two dollars per broadcast.
 */
export const STAGE_LABEL: Record<PipelineStage, string> = {
  transcript: 'Transcripción',
  topics: 'Señales y temas',
  claims: 'Afirmaciones normalizadas',
  prenota: 'Prenotas',
  x_review: 'Relevancia en X',
  topic_link: 'Temas canónicos',
  notas: 'Notas editoriales',
};

export const STAGE_PRODUCES: Record<PipelineStage, string> = {
  transcript: 'El audio convertido en texto, con las voces identificadas.',
  topics: 'Los temas y las afirmaciones en bruto de cada tramo.',
  claims: 'Cada afirmación reescrita en forma canónica, con su evidencia.',
  prenota: 'Las historias del stream, con su elegibilidad y sus formatos posibles.',
  x_review: 'Qué tanto le importa a la conversación pública cada historia.',
  topic_link: 'Cada historia ligada al tema que ya venía apareciendo en otras transmisiones.',
  notas: 'La nota redactada en cada perfil editorial, verificada y enriquecida.',
};

/** Stages that spend money outside the box. Marked in the picker, never hidden. */
export const PAID_STAGES: ReadonlySet<PipelineStage> = new Set<PipelineStage>(['x_review', 'notas']);

export const JOB_STATUS_LABEL: Record<PipelineJobStatus, string> = {
  pending: 'en cola',
  running: 'procesando',
  done: 'terminado',
  failed: 'falló',
  cancelled: 'cancelado',
};

export const JOB_STATUS_TONE: Record<PipelineJobStatus, Tone> = {
  pending: 'unresolved',
  running: 'partial',
  done: 'supported',
  failed: 'contradicted',
  cancelled: 'unresolved',
};

/**
 * The contiguous range a selection describes.
 *
 * A gap is closed rather than honoured, and the pipeline does the same on its side: asking for the
 * transcript and the notas without the prenota between them describes a run that cannot exist,
 * since the drafter reads what the prenota produced.
 */
export function stageRange(selected: PipelineStage[]): PipelineStage[] {
  if (selected.length === 0) return [];

  const indexes = selected.map(stage => PIPELINE_STAGES.indexOf(stage));
  const first = Math.min(...indexes);
  const last = Math.max(...indexes);

  return PIPELINE_STAGES.slice(first, last + 1);
}

/** Every stage up to and including this one — what "procesar hasta aquí" means. */
export function stagesUpTo(stage: PipelineStage): PipelineStage[] {
  return PIPELINE_STAGES.slice(0, PIPELINE_STAGES.indexOf(stage) + 1);
}

/**
 * Which stages this broadcast already produced something for.
 *
 * Read from the counted artifacts rather than from the job history on purpose: a stream processed
 * before the queue existed, or from the command line, has no job — and its transcript is just as
 * real.
 */
export function completedStages(summary: EditorialStreamSummary): ReadonlySet<PipelineStage> {
  const done = new Set<PipelineStage>();

  if (summary.turns > 0) done.add('transcript');
  if (summary.signals > 0) done.add('topics');
  if (summary.claims > 0) done.add('claims');
  if (summary.prenotas > 0) done.add('prenota');
  if (summary.xReviews > 0) done.add('x_review');
  if ((summary.topicMentions ?? 0) > 0) done.add('topic_link');
  if (summary.outputs > 0) done.add('notas');

  return done;
}

/** The first stage with nothing behind it — where "continuar" starts. */
export function nextStage(done: ReadonlySet<PipelineStage>): PipelineStage | null {
  return PIPELINE_STAGES.find(stage => !done.has(stage)) ?? null;
}

/**
 * Every stage a `streamId`-based "continuar" resubmission actually needs, from the first one this
 * broadcast is missing through `stopAfter`.
 *
 * Never starts at `transcript`: this is the resume path, submitted by `streamId` with no
 * `sourceUrl`, and the pipeline requires a source URL to restart the transcript stage — including
 * it here fails the job instead of resuming it.
 *
 * `topics` counts as unfinished whenever criteria were never evaluated, even when `done` already
 * has it from `signals > 0`: `evaluate_criteria` and `build_story_candidates` are a side effect of
 * the topics stage, not a separate one, so a broadcast whose criteria pass matched nothing needs
 * `topics` back in range for a second chance at story candidates. Reusing the stored signals costs
 * nothing extra — only the criteria judge calls a first pass would have made anyway.
 */
export function stagesToResume(
  stopAfter: PipelineStage,
  done: ReadonlySet<PipelineStage>,
  criteriaEvaluated: number
): PipelineStage[] {
  const needsTopics = criteriaEvaluated === 0;
  const resumable = PIPELINE_STAGES.filter(stage => stage !== 'transcript');
  const firstNeeded = resumable.find(stage => (stage === 'topics' && needsTopics) || !done.has(stage)) ?? stopAfter;
  const start = PIPELINE_STAGES.indexOf(firstNeeded) <= PIPELINE_STAGES.indexOf(stopAfter) ? firstNeeded : stopAfter;
  return stageRange([start, stopAfter]);
}

/** The job that decides what the screen says: the one in flight, or the most recent otherwise. */
export function activeJob(jobs: PipelineJob[]): PipelineJob | null {
  return jobs.find(job => job.status === 'running') ?? jobs.find(job => job.status === 'pending') ?? jobs[0] ?? null;
}

/**
 * A refused submission, said in the language of the screen it happened on.
 *
 * The API answers in English and its most common refusal — the video is already in the queue — is
 * not an error at all: it means the work is already going to happen. Rendering the raw sentence
 * made a normal situation read as a breakage, which is what an editor reported.
 */
export function describeSubmitFailure(error: unknown): { headline: string; detail: string; isAlreadyQueued: boolean } {
  const status = error instanceof ApiError ? error.status : 0;
  const detail = error instanceof Error ? error.message : '';

  if (status === 409 && /already in the queue/i.test(detail)) {
    return { headline: 'Este video ya está en la cola', detail: 'No hace falta volver a pedirlo: abajo puedes ver en qué va.', isAlreadyQueued: true };
  }
  if (status === 409) {
    return { headline: 'La cola no lo aceptó', detail, isAlreadyQueued: false };
  }
  if (status === 404) {
    return { headline: 'No se encontró la transmisión', detail, isAlreadyQueued: false };
  }
  if (status === 400) {
    return { headline: 'Revisa la liga del video', detail, isAlreadyQueued: false };
  }
  if (status === 0) {
    return { headline: 'No se pudo contactar al servidor', detail, isAlreadyQueued: false };
  }

  return { headline: 'No se pudo encolar el video', detail, isAlreadyQueued: false };
}
